import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import apiService from '../services/api';

interface Policy {
  id: number;
  title: string;
  content: string;
  slug: string;
  policy_type: string;
  meta_title?: string;
  meta_description?: string;
}

const PolicyPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [policy, setPolicy] = useState<Policy | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadPolicy = async () => {
      try {
        setLoading(true);
        
        if (!slug) {
          setError('Policy not specified');
          return;
        }

        // Convert legacy slug formats to current format
        let actualSlug = slug;
        if (slug === 'terms-conditions') {
          actualSlug = 'terms_conditions';
        } else if (slug === 'refund-policy') {
          actualSlug = 'refund_policy';
        }
        
        // Try to get policy by slug first, then fall back to legacy type-based lookup
        let response;
        try {
          response = await apiService.getPolicy(actualSlug);
        } catch (err) {
          // Fallback for legacy type-based lookup
          response = await apiService.getPolicyByType(actualSlug as any);
        }
        
        if (response.success) {
          setPolicy(response.data);
          // Update page title and meta tags
          if (response.data.meta_title) {
            document.title = response.data.meta_title;
          } else {
            document.title = response.data.title + ' - AARAN HOMES';
          }
          
          // Update meta description
          if (response.data.meta_description) {
            const metaDesc = document.querySelector('meta[name="description"]');
            if (metaDesc) {
              metaDesc.setAttribute('content', response.data.meta_description);
            }
          }
        } else {
          setError('Policy not found');
        }
      } catch (err) {
        console.error('Error loading policy:', err);
        setError('Failed to load policy');
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      loadPolicy();
    }
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (error || !policy) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-6xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Policy Not Found</h1>
          <p className="text-gray-600 mb-6">{error || 'The requested policy could not be found.'}</p>
          <a 
            href="/" 
            className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            ← Back to Home
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <nav className="mb-4">
            <a href="/" className="text-red-600 hover:text-red-700 text-sm font-medium">
              ← Back to Home
            </a>
          </nav>
          <h1 className="text-3xl font-bold text-gray-900">{policy.title}</h1>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <div 
            className="prose prose-lg max-w-none"
            dangerouslySetInnerHTML={{ __html: policy.content }}
          />
          
          {/* Footer */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <div className="text-sm text-gray-500">
              <p>Last updated: {new Date().toLocaleDateString()}</p>
              <p className="mt-2">
                If you have any questions about this policy, please contact us at{' '}
                <a href="mailto:info@aaranhomes.com" className="text-red-600 hover:text-red-700">
                  info@aaranhomes.com
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PolicyPage;